import { Bot, Users, Cpu, Play, Save, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import Toast from '../Toast';
import workflowService from '../../services/workflowService';
import { deepEqualArray } from '../../helpers/common';
import styled from 'styled-components';

const ToolbarShell = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;

  .toolbar-inner {
    display: flex;
    gap: 8px;
    padding: 8px;
    border-radius: 14px;
    border: 1px solid var(--line);
    background: var(--card);
    box-shadow: var(--shadow-lg);
  }

  .tool-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 56px;
    padding: 8px 10px;
    border-radius: 10px;
    border: 1px solid var(--line);
    background: var(--card-warm);
    color: var(--ink);
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    transition:
      border-color 0.15s ease,
      background 0.15s ease,
      color 0.15s ease;

    svg {
      width: 18px;
      height: 18px;
      color: var(--o2);
    }

    &:hover:not(:disabled) {
      border-color: var(--o1);
      background: var(--o-tint);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .spin {
      animation: wf-spin 0.8s linear infinite;
    }
  }

  @keyframes wf-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const NodeToolbar = ({ onAddNode, nodesData, edgesData, workflowId, isEdit, setSelectedNode }) => {
  const [loading, setLoading] = useState(false);
  const [savedSteps, setSavedSteps] = useState(isEdit?.steps || []);

  useEffect(() => {
    setSavedSteps(isEdit?.steps || []);
  }, [isEdit?.workflow_id, isEdit?.steps]);

  function generateSteps(nodes, edges) {
    const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));

    let steps = [];

    const startNode = nodes.find(n => n.data.type === 'start' || n.id === 'start-1');
    if (!startNode) return steps;

    let currentNodeId = startNode.id;

    while (true) {
      const nextEdge = edges.find(e => e.source === currentNodeId);
      if (!nextEdge) break;

      const nextNode = nodeMap[nextEdge.target];
      if (!nextNode) break;

      const stepData = nextNode.data;

      let formattedStep = {
        type: stepData?.type,
        step_name: stepData?.label || '',
        step_description: stepData?.instructions || stepData?.step_description || '',
      };

      if (stepData.type === 'agent') {
        if (typeof stepData?.agentId === 'object') {
          formattedStep.agent_id = stepData?.agentId?.value || '';
        } else {
          formattedStep.agent_id = stepData?.agent_id || stepData?.agentId || '';
        }
      }

      if (stepData.type === 'team') {
        if (typeof stepData?.teamId === 'object') {
          formattedStep.team_id = stepData?.teamId?.value || '';
        } else {
          formattedStep.team_id = stepData?.team_id || stepData?.teamId || '';
        }
      }

      if (stepData.type === 'llm') {
        if (typeof stepData?.model === 'object') {
          formattedStep.model = stepData?.model?.value || '';
        } else {
          formattedStep.model = stepData?.model || '';
        }
        formattedStep.prompt = stepData?.prompt || '';
      }

      steps.push(formattedStep);
      currentNodeId = nextNode.id;
    }

    return steps;
  }

  const handlePreview = async () => {
    setLoading(true);
    try {
      const steps = generateSteps(nodesData, edgesData);

      if (deepEqualArray(savedSteps, steps)) {
        Toast({
          type: 'info',
          message: 'No changes found!',
        });
        setLoading(false);
        return;
      }

      const res = await workflowService.updateWorkflow({
        steps,
        workflow_id: workflowId,
      });
      setSavedSteps(steps);
      Toast({
        type: 'success',
        message: res.detail || 'Workflow updated successfully!',
      });
      setLoading(false);
    } catch (error) {
      Toast({
        type: 'error',
        message: error?.detail || error?.message || 'Something went wrong!',
      });
      setLoading(false);
    }
  };

  return (
    <ToolbarShell>
      <div className="toolbar-inner">
        <button type="button" className="tool-btn" onClick={handlePreview} disabled={loading}>
          {loading ? <Loader2 className="spin" /> : <Save />}
          <span>Save</span>
        </button>

        <button
          type="button"
          className="tool-btn"
          onClick={() =>
            setSelectedNode({
              data: {
                type: 'chat',
                label: 'Chat',
              },
            })
          }>
          <Play />
          <span>Run</span>
        </button>

        <button type="button" className="tool-btn" onClick={() => onAddNode('agent')}>
          <Bot />
          <span>Agent</span>
        </button>

        <button type="button" className="tool-btn" onClick={() => onAddNode('team')}>
          <Users />
          <span>Team</span>
        </button>

        <button type="button" className="tool-btn" onClick={() => onAddNode('llm')}>
          <Cpu />
          <span>LLM</span>
        </button>
      </div>
    </ToolbarShell>
  );
};
